// src/features/messages/components/MessageThread.jsx
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DeleteOutline as DeleteIcon,
  DoneAll as ReadIcon,
  ExpandLess,
  ExpandMore,
  Send as SendIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ProfilePicture from '../../../components/common/ProfilePicture';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import AudienceLine from './AudienceLine';
import MessageTime from './MessageTime';
import ReactionBar from './ReactionBar';
import { MAX_CONTENT_LENGTH } from '../constants';

function Avatar({ staff, size }) {
  return (
    <ProfilePicture
      profilePictureFileId={staff?.profile_picture_file_id}
      name={staff?.name}
      color={staff?.color}
      size={size}
      imageSize="64x64"
      bgcolor="grey.400"
    />
  );
}

function ReadReceipts({ message }) {
  const { t } = useTranslation(['messages']);
  const [open, setOpen] = useState(false);
  const { read_count: read, recipient_count: total } = message;

  return (
    <Box>
      <Button
        size="small"
        color="inherit"
        startIcon={<ReadIcon color={read === total ? 'primary' : 'disabled'} />}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        onClick={() => setOpen(prev => !prev)}
        sx={{ color: 'text.secondary', textTransform: 'none', px: 1 }}
      >
        {read === total
          ? t('messages:thread.read_by_all', { count: total })
          : t('messages:thread.read_by', { read, total })}
      </Button>
      <Collapse in={open} unmountOnExit>
        <List dense disablePadding sx={{ maxHeight: 240, overflowY: 'auto' }}>
          {message.recipients.map(({ staff_member: staff, read_at: readAt }) => (
            <ListItem key={staff.staff_member_id} disableGutters sx={{ py: 0.25 }}>
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <Avatar staff={staff} size={28} />
              </ListItemAvatar>
              <ListItemText
                primary={staff.name}
                secondary={readAt
                  ? <>{t('messages:thread.read')} <MessageTime date={readAt} /></>
                  : t('messages:thread.not_read')}
                slotProps={{ secondary: { component: 'span', color: readAt ? 'text.secondary' : 'warning.main' } }}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Box>
  );
}

function CommentComposer({ onSubmit, disabled, autoFocus }) {
  const { t } = useTranslation(['messages']);
  const [text, setText] = useState('');
  const canSend = text.trim().length > 0 && !disabled;

  const submit = async () => {
    if (!canSend) return;
    if (await onSubmit(text.trim())) setText('');
  };

  return (
    <TextField
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          submit();
        }
      }}
      placeholder={t('messages:thread.comment_placeholder')}
      size="small"
      fullWidth
      multiline
      maxRows={5}
      autoFocus={autoFocus}
      slotProps={{
        htmlInput: { maxLength: MAX_CONTENT_LENGTH, 'aria-label': t('messages:thread.comment_placeholder') },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={t('messages:thread.send_comment')}>
                <span>
                  <IconButton size="small" color="primary" onClick={submit} disabled={!canSend} aria-label={t('messages:thread.send_comment')}>
                    <SendIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

/**
 * A message with its reactions, read receipts and comments.
 * `thread` is the result of useMessageThread.
 */
export default function MessageThread({ thread, prominentReactions = false, autoFocusComment = false }) {
  const { t } = useTranslation(['messages', 'common']);
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { message, busy, error, toggleReaction, addComment, deleteComment } = thread;

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!message) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const confirmDeleteComment = (comment) => showConfirmDialog({
    title: t('messages:thread.delete_comment_title'),
    message: comment.content,
    confirmText: t('common:buttons.delete'),
    severity: 'error',
    onConfirm: () => deleteComment(comment.comment_id),
  });

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Avatar staff={message.author} size={44} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {message.is_author ? t('messages:you') : (message.author?.name ?? t('messages:deleted_account'))}
            </Typography>
            <MessageTime date={message.created_at} />
          </Box>
          <AudienceLine audience={message.audience} />
        </Box>
      </Box>

      {/* The message is the point; it should outweigh the name, reactions and receipts around it */}
      <Typography variant="body1" sx={{ fontSize: '1.0625rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {message.content}
      </Typography>

      <ReactionBar
        reactions={message.reactions}
        onToggle={toggleReaction}
        disabled={busy}
        alwaysShowPicker={prominentReactions}
      />

      {/* Read receipts are for the author; the server only sends them there */}
      {message.is_author && message.recipient_count > 0 && <ReadReceipts message={message} />}

      <Divider />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('messages:thread.comments', { count: message.comment_count })}
        </Typography>
        {message.comments.length > 0 && (
          <Stack spacing={1.5} sx={{ mb: 1.5 }}>
            {message.comments.map(comment => (
              <Box key={comment.comment_id} sx={{ display: 'flex', gap: 1.25 }}>
                <Avatar staff={comment.author} size={30} />
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    bgcolor: 'action.hover',
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.75,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {comment.is_mine ? t('messages:you') : (comment.author?.name ?? t('messages:deleted_account'))}
                    </Typography>
                    <MessageTime date={comment.created_at} />
                    {comment.is_new && <Chip label={t('messages:thread.new')} size="small" color="primary" sx={{ height: 18, fontSize: '0.7rem' }} />}
                    <Box sx={{ flex: 1 }} />
                    {comment.is_mine && (
                      <Tooltip title={t('messages:thread.delete_comment')}>
                        <IconButton size="small" onClick={() => confirmDeleteComment(comment)} disabled={busy} aria-label={t('messages:thread.delete_comment')} sx={{ m: -0.5 }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {comment.content}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
        <CommentComposer onSubmit={addComment} disabled={busy} autoFocus={autoFocusComment} />
      </Box>
      <ConfirmDialog />
    </Stack>
  );
}
